/**
 * Staff access checks (no nav imports — safe for mainNav / settings gates).
 *
 * User field:
 *   staffAccess: { mode: 'full_admin' | 'custom', permissions: string[], preset? }
 *
 * `fulfiller_ops` = Member preset (warehouse / premade floor pack; editable).
 */

/** Nav permission pack for warehouse / premade floor ops. */
export const FULFILLER_OPS_PERMISSIONS = [
  "inventory-admin/stock",
  "inventory-admin/receiving",
  "inventory-admin/tasks",
  "inventory-admin/purchase-orders",
  "inventory-admin/activity",
  "3pl/enrollment",
  "3pl/retail-stock",
  "3pl/sellers",
  "3pl/sync-settings",
  // Legacy aliases (existing custom staff grants)
  "3pl/receiving",
  "3pl/seller-stock",
  "inventory-admin/retail-stock",
  "inventory-admin/warehouse-locations",
  "manufacturing/batches",
  "manufacturing/bins",
  "manufacturing/batch-by-items",
  "orders/ship",
];

export function isStaffAccessRestricted(user) {
  return user?.staffAccess?.mode === "custom";
}

export function isFulfillerOps(user) {
  if (!user || user.staffRevoked) return false;
  const roles = user.roles || [];
  if (roles.includes("fulfiller_ops") || roles.includes("fulfiller-ops")) {
    return true;
  }
  return user.staffAccess?.preset === "fulfiller_ops";
}

export function canAccessWarehouseOps(user) {
  if (!user || user.staffRevoked) return false;
  if (isFullStaffAdmin(user)) return true;
  if (isFulfillerOps(user)) return true;
  if (user.staffAccess?.mode === "custom") {
    const perms = user.staffAccess.permissions || [];
    return perms.some(
      (p) =>
        p === "inventory-admin/warehouse-locations" ||
        p === "inventory-admin" ||
        p === "3pl" ||
        (typeof p === "string" && p.startsWith("inventory-admin/")) ||
        (typeof p === "string" && p.startsWith("3pl/"))
    );
  }
  if (user.isEnterpriseAdmin && !user.staffAccess) return true;
  return false;
}

export function isFullStaffAdmin(user) {
  if (!user) return false;
  if (!user.staffAccess || user.staffAccess.mode == null) {
    return (
      !!user.isEnterpriseAdmin ||
      (user.roles || []).includes("admin") ||
      (user.roles || []).includes("super_admin")
    );
  }
  return user.staffAccess.mode === "full_admin";
}

/**
 * Who gets the enterprise-admin rail (NAV.1, operational dashboard, admin
 * nav visibility). Invited staff are flagged `isEnterpriseAdmin`; legacy
 * full admins may only have `roles: ["admin"]` / `staffAccess.full_admin`.
 * Custom-scoped staff stay on this rail so `applyStaffPermissionVisibility`
 * can narrow it — they are not demoted to the seller nav.
 */
export function isAdminNavUser(user) {
  if (!user || user.staffRevoked) return false;
  if (user.isEnterpriseAdmin) return true;
  return isFullStaffAdmin(user);
}

/** Account owners + full admins can invite; custom staff cannot. */
export function canInviteStaff(user) {
  if (!user) return false;
  if ((user.roles || []).includes("super_admin")) return true;
  if ((user.roles || []).includes("admin") && !isStaffAccessRestricted(user)) {
    return true;
  }
  if (user.isEnterpriseAdmin && !isStaffAccessRestricted(user)) return true;
  return false;
}

export function hasStaffPermission(user, permissionId) {
  if (!user) return false;
  const access = user.staffAccess;
  if (!access || access.mode == null) return true;
  if (access.mode === "full_admin") return true;
  if (access.mode !== "custom") return true;

  const perms = Array.isArray(access.permissions) ? access.permissions : [];
  if (!permissionId) return false;
  if (perms.includes(permissionId)) return true;
  // RTL.1 — legacy seller-stock / brief Inventory path still grant Retail stock.
  if (
    permissionId === "3pl/retail-stock" &&
    (perms.includes("3pl/seller-stock") ||
      perms.includes("inventory-admin/retail-stock"))
  ) {
    return true;
  }
  if (
    permissionId === "inventory-admin/receiving" &&
    perms.includes("3pl/receiving")
  ) {
    return true;
  }
  if (permissionId === "dst-library" && perms.includes("design-library")) {
    return true;
  }

  // Parent nav groups stay visible when any child leaf is granted.
  // Do NOT treat a parent id as "all children" — unchecked leaves like
  // draft-orders must stay denied even if `orders-admin` is in the list.
  const prefix = `${permissionId}/`;
  if (perms.some((p) => typeof p === "string" && p.startsWith(prefix))) {
    return true;
  }

  return false;
}

/**
 * TAX.1-B — tax determination edits (exempt / remove / correct rate).
 * Full admins always; custom staff need a tax/* grant. Enforced on the API too.
 */
export function canEditTax(user) {
  if (!user || user.staffRevoked) return false;
  if (isFullStaffAdmin(user)) return true;
  if (user.isEnterpriseAdmin && !user.staffAccess) return true;
  if (!isStaffAccessRestricted(user)) {
    const roles = user.roles || [];
    if (roles.includes("admin") || roles.includes("super_admin")) return true;
  }
  return (
    hasStaffPermission(user, "tax") ||
    hasStaffPermission(user, "tax/edit-tax") ||
    hasStaffPermission(user, "tax/sales-tax")
  );
}

export const ORDERS_CANCEL = "orders/cancel";
export const ORDERS_EDIT = "orders/edit";
export const ORDERS_SHIP = "orders/ship";
export const ORDER_CAPABILITY_PERMISSIONS = [
  ORDERS_CANCEL,
  ORDERS_EDIT,
  ORDERS_SHIP,
];

/**
 * Orders page access is view only. Cancel, edit, and ship are separate
 * grants. Custom access saved before these grants has no
 * `orderCapabilities` flag and keeps every order action.
 */
export function hasOrderCapability(user, capability) {
  if (!user || user.staffRevoked) return false;
  if (!isStaffAccessRestricted(user)) return true;
  if (!user.staffAccess.orderCapabilities) return true;
  const perms = Array.isArray(user.staffAccess.permissions)
    ? user.staffAccess.permissions
    : [];
  return perms.includes(capability);
}

export const canCancelOrders = (user) => hasOrderCapability(user, ORDERS_CANCEL);
export const canEditOrders = (user) => hasOrderCapability(user, ORDERS_EDIT);
export const canShipOrders = (user) => hasOrderCapability(user, ORDERS_SHIP);

/**
 * Permissions to show in the access editor. Access saved before the order
 * grants could do everything, so show those grants checked; saving then
 * keeps the same actions under the new rules.
 */
export function permissionsForAccessEditor(staffAccess) {
  const stored = Array.isArray(staffAccess?.permissions)
    ? staffAccess.permissions
    : [];
  if (staffAccess?.mode !== "custom" || staffAccess?.orderCapabilities) {
    return stored;
  }
  return [...new Set([...stored, ...ORDER_CAPABILITY_PERMISSIONS])];
}

/**
 * SO.1 Tier 2 — add supplemental charges to an approved sales order.
 * Full admins always; custom staff need sales-orders access or the explicit grant.
 */
export function canAddSupplementalCharge(user) {
  if (!user || user.staffRevoked) return false;
  if (isFullStaffAdmin(user)) return true;
  if (user.isEnterpriseAdmin && !user.staffAccess) return true;
  if (!isStaffAccessRestricted(user)) {
    const roles = user.roles || [];
    if (roles.includes("admin") || roles.includes("super_admin")) return true;
  }
  return (
    hasStaffPermission(user, "orders-admin/sales-orders") ||
    hasStaffPermission(user, "orders-flat/sales-orders") ||
    hasStaffPermission(user, "sales-orders/add-supplemental-charge")
  );
}

/** Always keep these visible so staff can reach their own account / help. */
const STAFF_ACCESS_ALWAYS = new Set(["profile", "settings", "help"]);

export function applyStaffPermissionVisibility(user, visibility) {
  if (!isStaffAccessRestricted(user) || !visibility) return visibility;
  const next = { ...visibility };
  Object.keys(next).forEach((id) => {
    if (next[id] === false) return;
    if (STAFF_ACCESS_ALWAYS.has(id)) return;
    if (!hasStaffPermission(user, id)) next[id] = false;
  });
  return next;
}

export function normalizeStaffAccess({ mode, permissions } = {}) {
  if (mode === "full_admin") {
    return { mode: "full_admin", permissions: [] };
  }
  if (mode === "fulfiller_ops" || mode === "fulfiller-ops") {
    const source =
      Array.isArray(permissions) && permissions.length
        ? permissions
        : FULFILLER_OPS_PERMISSIONS;
    const unique = [...new Set(source.filter(Boolean))];
    // Keep leaf grants only — drop parent ids when a child is also present
    // (e.g. drop `orders-admin` if `orders-admin/orders` is stored).
    const list = unique.filter(
      (id) =>
        !unique.some(
          (other) => other !== id && String(other).startsWith(`${id}/`)
        )
    );
    return {
      mode: "custom",
      permissions: list.length ? list : [...FULFILLER_OPS_PERMISSIONS],
      preset: "fulfiller_ops",
      orderCapabilities: true,
    };
  }
  const unique = Array.isArray(permissions)
    ? [...new Set(permissions.filter(Boolean))]
    : [];
  // Keep leaf grants only — drop parent ids when a child is also present
  // (e.g. drop `orders-admin` if `orders-admin/orders` is stored).
  const list = unique.filter(
    (id) =>
      !unique.some(
        (other) => other !== id && String(other).startsWith(`${id}/`)
      )
  );
  return { mode: "custom", permissions: list, orderCapabilities: true };
}
